import { useCallback } from 'react';
import { useSwipeable } from 'react-swipeable';

/**
 * Custom hook to handle swipe gestures for mobile navigation
 * 
 * @param {Object} options - Configuration options
 * @param {boolean} options.isMobileView - Whether mobile view is active 
 * @param {Function} options.onSwipeLeft - Function to call on left swipe
 * @param {Function} options.onSwipeRight - Function to call on right swipe
 * @param {Function} options.onSwipeUp - Function to call on up swipe
 * @param {Function} options.onSwipeDown - Function to call on down swipe
 * @returns {Object} - Swipe handlers to spread on a component
 */
const useSwipeNavigation = ({
  isMobileView = false,
  onSwipeLeft = () => {},
  onSwipeRight = () => {},
  onSwipeUp = () => {},
  onSwipeDown = () => {},
}) => {
  
  // Left/right handlers for model selection
  const handleSwipeLeft = useCallback(() => {
    if (isMobileView) {
      onSwipeLeft();
    }
  }, [isMobileView, onSwipeLeft]);

  const handleSwipeRight = useCallback(() => {
    if (isMobileView) {
      onSwipeRight();
    }
  }, [isMobileView, onSwipeRight]);

  // Up/down handlers for tab navigation
  const handleSwipeUp = useCallback(() => {
    if (isMobileView) {
      onSwipeUp();
    }
  }, [isMobileView, onSwipeUp]);

  const handleSwipeDown = useCallback(() => {
    if (isMobileView) {
      onSwipeDown();
    }
  }, [isMobileView, onSwipeDown]);

  // Generate swipe handlers with improved settings
  const swipeHandlers = useSwipeable({
    onSwipedLeft: handleSwipeLeft,
    onSwipedRight: handleSwipeRight,
    onSwipedUp: handleSwipeUp,
    onSwipedDown: handleSwipeDown,
    preventDefaultTouchmoveEvent: false, // Allow default scrolling
    trackMouse: false,
    delta: 50, // Increase minimum distance to trigger swipe (default is 10px)
    swipeDuration: 250, // Reduce duration of swipe to make it less sensitive to slow scrolls
    touchEventOptions: { passive: true } // Improve performance by signaling it won't prevent scrolling
  });

  return swipeHandlers;
};

export default useSwipeNavigation;
